import {
  ActionIcon,
  CloseButton,
  Collapse,
  Group,
  Progress,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
  VisuallyHidden,
} from "@mantine/core";
import { CheckCircle2, ChevronDown, ChevronUp, Image, Music, RefreshCw, Video } from "lucide-react";
import { useLayoutEffect, useState } from "react";

import {
  canRetry,
  isActive,
  type UploadEntry,
  type UploadQueue,
  type UploadStatus,
} from "./upload-queue";
import classes from "./UploadDialog.module.css";

/** What a row says beside the size while the file is not simply sending. */
const STATUS_TEXT: Record<UploadStatus, string> = {
  waiting: "Waiting",
  preparing: "Preparing",
  sending: "Uploading",
  completing: "Finishing",
  done: "Uploaded",
  failed: "Failed",
  canceled: "Canceled",
};

/**
 * A file's size: live's megabytes with one decimal from 1 MB up; below it, whole kilobytes (or
 * bytes), where live's reads "0.0 MB" (the demo sweep, #828 item 8).
 */
function fileSize(bytes: number): string {
  const kilobytes = Math.round(bytes / 1024);
  if (kilobytes >= 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return bytes < 1024 ? `${bytes} B` : `${kilobytes} KB`;
}

/** "N file(s)". */
function files(count: number): string {
  return `${count} ${count === 1 ? "file" : "files"}`;
}

/**
 * The box's header: live's "Uploading N files" while any file is on its way; once none is, what
 * came of them, where live's keeps saying "Uploading" (#828 item 8): "Uploaded N files", or
 * "1 of 2 files uploaded" when one failed or was canceled.
 */
function header(entries: readonly UploadEntry[], active: number, done: number): string {
  if (active > 0) return `Uploading ${files(entries.length)}`;
  if (done === entries.length) return `Uploaded ${files(done)}`;
  return `${done} of ${files(entries.length)} uploaded`;
}

function TypeIcon({ file }: { file: File }) {
  if (file.type.startsWith("video/")) return <Video size={24} aria-hidden />;
  if (file.type.startsWith("audio/")) return <Music size={24} aria-hidden />;
  return <Image size={24} aria-hidden />;
}

/**
 * The upload box, as live shows it (design-system.md 3.5): fixed at the bottom right, 490px,
 * "Uploading N files" (what came of them once none is on its way) with a chevron that folds it,
 * "Cancel all", and one row per file. It shows once a file is chosen; when every file is done,
 * "Clear" empties it and it goes. It tells `onTop` how far it reaches up from the window's bottom
 * edge, 0 once it goes, so the unlock card stands above it (spec web/unlock-card-above-uploads).
 */
export function UploadDialog({ queue, onTop }: { queue: UploadQueue; onTop: (top: number) => void }) {
  const [collapsed, setCollapsed] = useState(false);
  const [box, setBox] = useState<HTMLElement | null>(null);
  const { entries } = queue;
  // A new batch opens the box again, even if it was folded for the last one.
  const [count, setCount] = useState(entries.length);
  if (entries.length !== count) {
    setCount(entries.length);
    if (entries.length > count) setCollapsed(false);
  }
  // Measured before the browser paints, as the box shows, and again at each size it takes (folded,
  // a row more, a narrower window), so the card never covers it for a frame.
  useLayoutEffect(() => {
    if (box === null) return;
    const measure = () => onTop(document.documentElement.clientHeight - box.getBoundingClientRect().top);
    measure();
    const resized = new ResizeObserver(measure);
    resized.observe(box);
    return () => {
      resized.disconnect();
      onTop(0);
    };
  }, [box, onTop]);
  if (entries.length === 0) return null;
  const active = entries.filter(isActive).length;
  const done = entries.filter((entry) => entry.status === "done").length;
  const failed = entries.filter((entry) => entry.status === "failed").length;
  return (
    <section ref={setBox} className={classes.box} data-collapsed={collapsed || undefined} aria-label="Uploads">
      <Group justify="space-between" wrap="nowrap">
        <Text fw={600}>{header(entries, active, done)}</Text>
        <ActionIcon
          variant="subtle"
          radius="xl"
          aria-label={collapsed ? "Show the uploads" : "Fold the uploads"}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </ActionIcon>
      </Group>
      {/* One live region for the batch, so a screen reader hears progress without every row. */}
      <VisuallyHidden component="output" aria-live="polite">
        {`${done} of ${entries.length} files uploaded${failed > 0 ? `, ${failed} failed` : ""}.`}
      </VisuallyHidden>
      <Collapse expanded={!collapsed}>
        <Group justify="flex-end" mb={8} pr={10}>
          {active > 0 ? (
            <UnstyledButton className={classes.link} onClick={queue.cancelAll}>
              Cancel all
            </UnstyledButton>
          ) : (
            <UnstyledButton className={classes.link} onClick={queue.clear}>
              Clear
            </UnstyledButton>
          )}
        </Group>
        <ScrollArea.Autosize mah={400} offsetScrollbars scrollbarSize={10}>
          {entries.map((entry) => (
            <UploadRow key={entry.id} entry={entry} queue={queue} />
          ))}
        </ScrollArea.Autosize>
      </Collapse>
    </section>
  );
}

function UploadRow({ entry, queue }: { entry: UploadEntry; queue: UploadQueue }) {
  const { file } = entry;
  const busy = isActive(entry);
  const renamed = entry.savedAs !== undefined && entry.savedAs !== file.name.trim();
  return (
    <div className={classes.row} data-failed={entry.status === "failed" || undefined}>
      <div className={classes.line}>
        <TypeIcon file={file} />
        <div className={classes.meta}>
          <Tooltip label={file.name} withArrow position="top-start">
            <Text size="sm" fw={500} lineClamp={1}>
              {file.name}
            </Text>
          </Tooltip>
          <Text size="xs" c="var(--cai-color-text-muted)">
            {fileSize(file.size)}
            {entry.status === "sending" ? null : (
              <>
                {" · "}
                <span>{STATUS_TEXT[entry.status]}</span>
              </>
            )}
          </Text>
          {renamed ? (
            <Text size="xs" c="var(--cai-color-text-muted)">
              Saved as {entry.savedAs}
            </Text>
          ) : null}
          {entry.problem === undefined ? null : (
            <Text size="xs" c="var(--cai-color-text-danger)" role="alert">
              {entry.problem}
            </Text>
          )}
        </div>
        {entry.status === "done" ? (
          <CheckCircle2 size={24} className={classes.done} aria-hidden />
        ) : null}
        {canRetry(entry) ? (
          <ActionIcon
            variant="subtle"
            radius="xl"
            aria-label={`Try again: ${file.name}`}
            onClick={() => queue.retry(entry)}
          >
            <RefreshCw size={20} />
          </ActionIcon>
        ) : null}
        {entry.status === "done" ? null : busy ? (
          <CloseButton
            radius="xl"
            aria-label={`Cancel ${file.name}`}
            onClick={() => queue.cancel(entry)}
          />
        ) : (
          <CloseButton
            radius="xl"
            aria-label={`Remove ${file.name}`}
            onClick={() => queue.remove(entry)}
          />
        )}
      </div>
      {entry.status === "sending" ? (
        <Progress
          value={Math.round(entry.progress * 100)}
          size="sm"
          radius="xs"
          animated
          mr={20}
          aria-label={`Upload of ${file.name}`}
        />
      ) : null}
    </div>
  );
}
